import { onSyncStatus } from '../data/sync';
import { shell } from '../app';
import { readStoredSignals } from '../data/signals';
import { getSnapshot, onStoreChange } from '../data/store';
import { dayHeading, todayLocal } from '../logic/dates';
import type { Period } from '../logic/home';
import { segmented } from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import type { View } from '../ui/router';
import { linkBtn } from '../ui/components';
import { agendaCard } from './overview/agendaCard';
import { cachedSignalsAt } from './shared/noLeadsYet';
import { businessCard } from './overview/businessCard';
import { contractsSoonCard } from './overview/contractsSoonCard';
import { kpiRow } from './overview/kpiRow';
import { pipelineCard } from './overview/pipelineCard';

const PERIODS: readonly { value: Period; label: string }[] = [
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
];

/** The desktop Overview (D65): the period's numbers; today with its map beside the newest businesses;
 *  then the pipeline, the days and the contracts — the phone Home's content, laid out wide. */
export const overviewView: View = ({ root }) => {
  shell.setChrome(true);
  const host = h('div', { class: 'page page--overview' });
  root.appendChild(host);
  let period: Period = 'week';
  let disposed = false;
  let sequence = 0;
  const agenda = agendaCard();
  const business = businessCard();

  const render = async () => {
    if (disposed) return;
    const mine = ++sequence;
    const today = todayLocal(); // per render — a window left open past midnight must not show yesterday
    shell.setTitle('Overview', {
      subtitle: dayHeading(today),
      action: segmented<Period>({
        options: PERIODS,
        value: period,
        onChange: (p) => {
          period = p;
          void render();
        },
        size: 'md',
        columns: 2,
      }),
    });
    const [snap, stored] = await Promise.all([getSnapshot(), readStoredSignals()]);
    if (disposed || mine !== sequence) return;
    business.update(period, today);
    replaceChildren(
      host,
      kpiRow(snap, today, period),
      h('div', { class: 'ov-row ov-row--today' }, agenda.render(snap, today, { signalsAt: cachedSignalsAt(stored) }), business.el),
      h(
        'div',
        { class: 'ov-row ov-row--three' },
        pipelineCard(snap.leads),
        linkBtn('Visits by day · Reports', '#/dashboard', { variant: 'ghost' }),
        contractsSoonCard(snap.leads, today),
      ),
    );
  };

  void render();
  const offSync = onSyncStatus(() => void render());
  const off = onStoreChange(() => void render());
  return () => {
    disposed = true;
    off();
    offSync();
    agenda.dispose();
    business.dispose();
  };
};
